import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import { fetchDropdownValues } from "../../../common_nextgen/common_components/dropdown/DropdownHelper";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";
import { ROUTE_PATHS, PRICE_SCHEDULE_MODES } from "../../../common_nextgen/utils/Constants";
import getBackendUrl from "../../../environment/BackendService";
import { downloadCSV } from "../../../common_nextgen/utils/UtilFunctions";
import {
  useLocationState,
  useReceiveExternalMessages,
} from "../../../common_nextgen/common_components/common-hooks/useLocationState";
import useAiProgramBackNavigation from "../../../common_nextgen/common_components/common-hooks/useAiProgramBackNavigation";
import { useRBAC } from "../../../common_nextgen/hooks/RBACContext";

const useBenefitMaxScheduleSteps = () => {
  const [data, setData] = useState([]); /* Benefit Max Schedule Steps Data */
  const {
    BENEFIT_MAX_STEP_SCHEDULE_ADD,
    BENEFIT_MAX_STEP_SCHEDULE_EDIT,
    BENEFIT_MAX_STEP_SCHEDULE_DISPLAY,
    BENEFIT_ALT_COPAY_FORM_EDIT,
    BENEFIT_MAX_SCHEDULE,
  } = ROUTE_PATHS;
  const { writeAccess } = useRBAC();
  const location = useLocation();
  /*const { state: stateVal } = location;
  let [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);*/

  const { currentState, previousState, setStateVal } = useLocationState({
    initialState: location.state || [],
  });
  /* const [stateVal, setStateVal] = useState(location.state ?? []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return { currentState, previousState };
  }, [stateVal]);*/
  const {
    selectedPriceSchedules = [],
    previousScreen = BENEFIT_MAX_SCHEDULE,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
    message = "",
    returnProgramCode = "",
    mediatorId = "",
    paramsData: initialParamsData = {
      bscScheduleName: selectedPriceSchedules[initialIndex]?.bscScheduleName,
      description: selectedPriceSchedules[initialIndex]?.bscDescription,
      basis: selectedPriceSchedules[initialIndex]?.bscBasis,
      steps: selectedPriceSchedules[initialIndex]?.wrkNumber,
    },
  } = currentState;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [externalMode, setExternalMode] = useState(initialExternalMode);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowData, setRowData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigateToPage = useCustomNavigate();
  const [dropdownValues, setDropdownValues] = useState({});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("no");
  const toast = useToast();
  const { axios_helper_no_header } = useAxios();

  const [paramsData, setParamsData] = useState(initialParamsData);

  // Handles the "navigate-to-ai-new-program" cross-slice jump message relayed by the
  // NextGen-UI shell (e.g. from RCBSC005's "Steps" grid action) - mirrors the same
  // handling useCrossAppSlices() does internally for single-record form pages, kept
  // on this page's native useLocationState pattern since this is a search/list screen.
  useReceiveExternalMessages(
    setStateVal,
    setExternalMode,
    (data, message = "") => {
      if (
        message === "navigate-to-ai-new-program" &&
        Object.keys(data ?? {}).length
      ) {
        const {
          programCode = "",
          parameters: {
            screenParams = {},
            mediatorId = "",
            returnProgramCode = "",
          } = {},
        } = data;
        setParamsData(screenParams);
        setCurrentIndex(0);
        setStateVal([
          {
            message,
            programCode,
            returnProgramCode,
            selectedPriceSchedules: [screenParams],
            mediatorId,
          },
        ]);
        // screenParams only carries bscScheduleName (the grid-row key sent by
        // RCBSC005's base.tsx), same as every other cross-slice jump.
      }
    }
  );

  const { isAiProgramReturn, handleBackToAiProgram } = useAiProgramBackNavigation({
    programCode: returnProgramCode,
    mediatorId,
    paramsData,
  });

  const [fetchDataParam, setFetchDataParam] = useState({
    bscScheduleName: selectedPriceSchedules[currentIndex],
  });

  const screen_title = "Benefit Max Schedule Steps";
  const screenNumber = "RCBSD001";

  /* Search form fields that needs to be shown for filtering */
  const formFields = [
    {
      fieldLabel: "Schedule",
      name: "bscScheduleName",
      label: "Schedule",
      type: "text",
      fieldNature: "readOnly",

      maxLength: 10,
    },
    {
      fieldLabel: "Description",
      name: "bscDescription",
      label: "Description",
      type: "text",
      fieldNature: "readOnly",
      maxLength: 40,
    },
    {
      fieldLabel: "BSC Basis*Valid values",
      name: "bscBasis",
      label: "Basis",
      type: "dropdown",
      fieldNature: "readOnly",
      maxLength: 2,
    },
    {
      fieldLabel: "Steps",
      name: "wrkNumber",
      label: "Steps",
      type: "text",
      fieldNature: "readOnly",
      maxLength: 2,
    },
  ];

  /* Dropdown options fields that needs to be fetched with API */
  const dropdownOptions = [
    { field_label: "BSC Basis", condition: "*Valid values" },
  ];

  // const handleBack = () => {
  //   navigateToPage(previousScreen, previousState);
  //   // navigateToPage(previousScreen, {});
  // };

  const validateCopayOverride = (displayMode) => {
    const params = {
      bscScheduleName: paramsData.bscScheduleName,
      bsdStepNumber: rowData.bsdStepNumber,
    };
    axios_helper_no_header(
      "GET",
      "api/bsdBenefitScheduleDtl/validateBeforeAlternateCopays",
      null,
      params
    )
      .then((response) => {
        navigateToPage(BENEFIT_ALT_COPAY_FORM_EDIT, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            selectedPriceSchedules: selectedRows.length ? selectedRows : [rowData],
            previousScreen: location.pathname,
            scheduleName: paramsData.bscScheduleName,
            paramsData: paramsData,
            currentIndex,
            currentMode: displayMode,
          },
        ]);
      })
      .catch((error) => {
        console.error("There was an error editing the data!", error);
        const { responseStatus: { displayErrorFields = null, message } = {} } = error.data;
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      });
  };

  const alternateCopayOverRideValidation = async () => {
    if (!writeAccess) {
      const params = {
        bscScheduleName: paramsData.bscScheduleName,
        bsdStepNumber: rowData?.bsdStepNumber,
      };
      const url = `api/bacBenefitMaxAltCopay/initialFormData/RCBAC001`;
      const { responseData } = await axios_helper_no_header("GET", url, {}, params);
      if (!responseData.bmdScheduleCode) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "Alternate Copay Schedule not present",
          life: 3000,
        });
      } else {
        validateCopayOverride(PRICE_SCHEDULE_MODES.READ_ONLY);
      }
    } else {
      validateCopayOverride();
    }
  };

  /* Top Left Back button info that needs to be displayed on the page*/
  const leftButtons = [
    {
      button_label: "Back",
      button_handeler: () => {
        if (externalMode) {
          const message = {
            message: "navigate-to-external-program",
            url: previousScreen,
            parameters: {
              state: [...previousState],
            },
          };
          console.log("back messages", message);
          window.parent.postMessage(message, "*");
        } else if (currentIndex < selectedPriceSchedules.length - 1) {
          setCurrentIndex((prevIndex) => prevIndex + 1);
        } else if (message === "navigate-to-ai-new-program" && isAiProgramReturn) {
          handleBackToAiProgram();
        } else {
          navigateToPage(previousScreen, previousState);
        }
      },
      button_key: "F12",
    },
  ];

  const dropdownCondition = {
    dropDowns: dropdownOptions.map((item) => item.field_label + item.condition),
  };

  /* ACTION BUTTONS info that needs to be displayed on the page */
  const ctaButtons = [
    {
      button_label: "Add Benefit Max Schedule Steps",
      button_handeler: () => {
        navigateToPage(BENEFIT_MAX_STEP_SCHEDULE_ADD, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            previousScreen: location.pathname,
            scheduleName: paramsData.bscScheduleName,
          },
        ]);
      },
      button_key: "F6",
    },
    {
      button_label: "Export as CSV",
      button_handeler: () => {
        exportAsCSV();
      },
      button_key: "",
    },
  ];

  /* Gear Icon Menu option CTAs */
  const menuOptions = [
    {
      label: "Edit",
      icon: <span className="material-icons">edit</span>,
      command: () => {
        navigateToPage(BENEFIT_MAX_STEP_SCHEDULE_EDIT, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPriceSchedules: selectedRows.length
              ? selectedRows
              : [rowData],
            previousScreen: location.pathname,
            scheduleName: paramsData.bscScheduleName,
            currentIndex: 0,
            paramsData: paramsData,
          },
        ]);
      },
    },
    {
      label: "Delete",
      icon: <span className="material-icons">delete</span>,
      command: () => {
        openConfirmationModal();
      },
    },
    {
      label: "Display",
      icon: <span className="material-icons">visibility</span>,
      command: () => {
        navigateToPage(BENEFIT_MAX_STEP_SCHEDULE_DISPLAY, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPriceSchedules: selectedRows.length
              ? selectedRows
              : [rowData],
            previousScreen: location.pathname,
            scheduleName: paramsData.bscScheduleName,
            currentIndex: 0,
            paramsData: paramsData,
          },
        ]);
      },
    },
    {
      label: "Alternate Copays",
      icon: <span className="material-icons">swap_horiz</span>,
      command: () => {
        console.log("parent", selectedRows);
        alternateCopayOverRideValidation();
      },
    },
  ];

  const columnDef = [
    { header: "Step", field: "bsdStepNumber" },
    { header: "Benefit Max Individual", field: "bsdMbrBenefitAmount" },
    { header: "Benefit Max Family", field: "bsdFamBenefitAmount" },
    { header: "Patient Table", field: "bsdPricingTableFlag" },
    { header: "Brand Copay Amount", field: "bsdBrandCopayAmount" },
    { header: "Generic Copay Amount", field: "bsdGenericCopayAmount" },
    { header: "Brand Copay Percent", field: "bsdBrandPercentAmt" },
    { header: "Generic Copay Percent", field: "bsdGenericPercentAmt" },
    { header: "Basis", field: "bsdPercentBasis" },
    { header: "Message code", field: "bsdMessageCde" },
    { header: "Update", field: "bsdCopayBenefitFlag" },
  ];

  //   /* Param Key which used as bridge between various operations on tabluar data */
  // const parmKey1 = "bscScheduleName";
  const parmkey = "bsdStepNumber";

  /* Handler that fetches the data */
  const fetchData = async () => {
    const page = first / rows;
    try {
      const hasValidParams =
        paramsData &&
        Object.keys(paramsData).length &&
        paramsData.bscScheduleName;
      if (hasValidParams) {
        const newParams = { bscScheduleName: paramsData.bscScheduleName };
        const raw_response = await axios_helper_no_header(
          "GET",
          `api/bsdBenefitScheduleDtl/fetchStepsData`,
          null,
          {
            page: page,
            size: rows,
            ...newParams,
          }
        );
        const response = raw_response.responseData;
        const pageResponse = raw_response.responsePaginationDTO;
        if (response.length === 0) {
          setData([]);
          setTotalRecords(0);
          toast.current.show({
            severity: "info",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
        } else {
          setData(response);
          setTotalRecords(pageResponse.totalRecords); // Assuming the API returns totalRecords
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }
  };

  const fetchDropdownData = async (dataDropdown) => {
    try {
      const response = await fetchDropdownValues(dataDropdown);
      setDropdownValues(response);
    } catch (error) {
      console.error("Error fetching dropdown values:", error);
    }
  };

  const fetchSubScreenInfo = (paramsData) => {
    const params = { notesRequiredFlag: false };
    const hasValidParams =
      paramsData &&
      Object.keys(paramsData).length &&
      paramsData.bscScheduleName;
    if (hasValidParams) {
      axios_helper_no_header(
        "GET",
        "api/bscBenefitSchCtrl/" +
          encodeURIComponent(
            selectedPriceSchedules[currentIndex]?.bscScheduleName
          ),
        null,
        params
      )
        .then((response) => {
          fetchDisplayParamsData(response.responseData);
        })
        .catch((error) => {
          const errorMessage =
            error.data?.responseStatus?.message || error.message;
          console.error("There was an error fetching the data!", error);
          toast.current.show({
            severity: "error",
            summary: "",
            detail: errorMessage,
            life: 3000,
          });
          closeConfirmationModal();
        });
    }
  };

  const fetchDisplayParamsData = (response) => {
    setParamsData({
      ...paramsData,
      bscScheduleName: response.bscScheduleName,
      bscDescription: response.bscDescription,
      bscBasis: response.bscBasis,
      wrkNumber: response.wrkNumber,
    });
  };

  useEffect(() => {
    fetchDropdownData(dropdownCondition);
  }, []);

  useEffect(() => {
    fetchData(); // Initial fetch with page 0 and page size 10
  }, [paramsData, rows, currentPage, currentIndex, externalMode]);
  useEffect(() => {
    if (!paramsData.bscScheduleName) {
      setParamsData(initialParamsData);
      fetchSubScreenInfo(initialParamsData);
    }
  }, [initialParamsData]);

  const onPageChange = (event) => {
    console.log("page change", event.first, event);
    setFirst(event.first);
    setCurrentPage(event.page);
    setRows(event.rows);
  };

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  useEffect(() => {
    fetchSubScreenInfo(initialParamsData);
  }, [currentIndex]);

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedOption("no");
  };

  const deleteBenefitMaxScheduleStep = () => {
    if (selectedOption === "yes") {
      const params = {
        bscScheduleName: paramsData.bscScheduleName,
        bsdStepNumber: rowData[parmkey],
      };
      axios_helper_no_header(
        "DELETE",
        "api/bsdBenefitScheduleDtl",
        null,
        params
      )
        .then((response) => {
          const updatedItems = data.filter(
            (item) => item[paramsData.bscScheduleName] !== rowData
          );
          setTotalRecords((prevTotal) => prevTotal - 1);
          setData(updatedItems);
          toast.current.show({
            severity: "success",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
          closeConfirmationModal();
          if (currentPage > 0) {
            setFirst(0);
            setCurrentPage(0);
          } else {
            fetchData();
          }
        })
        .catch((error) => {
          console.error("There was an error deleting the data!", error);
          const {
            responseStatus: { displayErrorFields = null, message } = {},
          } = error.data;
          toast.current.show({
            severity: "error",
            summary: "",
            detail: message,
            life: 3000,
          });
          closeConfirmationModal();
        });
    } else {
      closeConfirmationModal();
    }
  };

  const exportAsCSV = async () => {
    const queryParams = { ...paramsData };
    try {
      const response = await axios_helper_no_header(
        "GET",
        "api/bsdBenefitScheduleDtl/exportCsvForStepsData",
        null,
        queryParams,
        "blob"
      );
      downloadCSV(response);
    } catch (error) {
      console.error("Error exporting as CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: "Failed to export CSV. Please try again.",
        life: 3000,
      });
    }
  };

  const updateParamData = (data) => {
    setFirst(0);
    setCurrentPage(0);
    setParamsData(data);
  };

  return {
    screenNumber,
    screen_title,
    formFields,
    dropdownValues,
    paramsData,
    updateParamData,
    ctaButtons,
    leftButtons,
    data,
    parmkey,
    menuOptions,
    columnDef,
    setRowData,
    selectedRows,
    setSelectedRows,
    first,
    rows,
    totalRecords,
    currentPage,
    onPageChange,
    isConfirmationModalOpen,
    closeConfirmationModal,
    deleteBenefitMaxScheduleStep,
    setSelectedOption,
  };
};

export default useBenefitMaxScheduleSteps;
